import React from 'react';

const RESOLUTION_PRESETS = [
  'Issue resolved & verified with user',
  'Workaround deployed successfully',
  'Customer confirmed resolution',
  'Config updated & patch applied',
  'Duplicate ticket / superseded'
];

const CloseTicketModal = ({
  isOpen,
  ticket,
  closeComment,
  setCloseComment,
  onClose,
  handleCloseTicket,
  isLoading,
}) => {
  if (!isOpen || !ticket) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="fixed inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800 animate-fadeIn z-10">
        
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 bg-emerald-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shadow-2xs flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-white tracking-tight">Close Ticket</h3>
              <p className="text-[10px] text-emerald-100 font-mono">
                #{ticket.id?.slice(0, 8)} • Official Resolution
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 text-xs">
          
          {/* Target Ticket Context Card */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
              Ticket Being Resolved
            </span>
            <div className="font-bold text-slate-900 text-xs sm:text-sm line-clamp-1">{ticket.title}</div>
            <div className="text-[10px] text-slate-500 flex items-center gap-2 pt-0.5">
              <span>Reported by: <strong className="text-slate-700">{ticket.created_by_username || 'User'}</strong></span>
              <span>•</span>
              <span>Priority: <strong className="text-slate-700 capitalize">{ticket.priority || 'medium'}</strong></span>
            </div>
          </div>

          {/* Quick Resolution Preset Chips */}
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              Quick Resolution Notes:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {RESOLUTION_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setCloseComment(preset)}
                  className="px-2.5 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 text-slate-600 border border-slate-200/80 rounded-lg text-[10px] font-medium transition-colors text-left"
                >
                  +{preset}
                </button>
              ))}
            </div>
          </div>

          {/* Resolution Note Textarea */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Resolution Note / Close Comment <span className="text-rose-500">*</span>
            </label>
            <textarea
              required
              rows={3}
              className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-emerald-500 rounded-xl p-3 text-xs text-slate-800 focus:outline-none placeholder-slate-400 leading-relaxed transition-all"
              placeholder="Provide a detailed explanation of the fix or resolution for customer records..."
              value={closeComment}
              onChange={(e) => setCloseComment(e.target.value)}
            />
            <p className="text-[10px] text-slate-400 mt-1">
              This note will be recorded permanently in the customer tracking portal and closed tickets archive.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCloseTicket}
              disabled={isLoading || !closeComment.trim()}
              className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 rounded-xl shadow-xs disabled:opacity-50 transition-all flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isLoading ? 'Closing Ticket...' : 'Close Ticket'}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
};

export default CloseTicketModal;
